import { useMemo, useRef, useEffect } from "react";
import { Vector3, BufferGeometry, Float32BufferAttribute } from "three";
import { useThree } from "@react-three/fiber";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import type { CenterlineData } from "./useTunnelCenterline";

/** Salvo hole data as loaded from JSON (generated by scripts/) */
export interface SalvoJsonHole {
  x: number;
  y: number;
  depth: number;
  spreadX: number;
  spreadY: number;
  type: "cut" | "stoping" | "contour" | "injection";
}

export interface SalvoJsonEntry {
  scan: number;
  faceStation: number;
  salvoDepth: number;
  contour: [number, number][];
  holes: SalvoJsonHole[];
}

export interface SalvoJsonData {
  salvos: SalvoJsonEntry[];
}

/** MWD raw data per scan (loaded from scan-N-mwd.json) */
export interface MwdRawHole {
  index: number;
  x: number;
  y: number;
  type: string;
  depth: number;
  depths: number[];
  values: Record<string, number[] | string[]>;
}

export interface MwdRawScanData {
  scan: number;
  sensors: string[];
  holes: MwdRawHole[];
}

export const MWD_SENSORS = [
  "PenetrNorm", "PenetrRMS",
  "RotaPressNorm", "RotaPressRMS",
  "FeedPressNorm",
  "HammerPressNorm",
  "WaterFlowNorm", "WaterFlowRMS",
] as const;

export type MwdSensorName = typeof MWD_SENSORS[number];

const TYPE_COLORS: Record<string, [number, number, number]> = {
  cut: [1, 0.3, 0.3],
  stoping: [0.3, 0.7, 1.0],
  contour: [0.2, 1, 0.4],
  injection: [1, 0.8, 0.1],
};

const FRAME_SAMPLES = 1000;

/**
 * Whether higher sensor values indicate worse rock quality.
 * Penetration: high = soft/weak rock (bad). Water flow: high = fractured (bad).
 * Rotation/feed/hammer pressure: high = hard rock (good).
 */
export const SENSOR_HIGH_IS_BAD: Record<string, boolean> = {
  PenetrNorm: true,
  PenetrRMS: true,
  RotaPressNorm: false,
  RotaPressRMS: false,
  FeedPressNorm: false,
  HammerPressNorm: false,
  WaterFlowNorm: true,
  WaterFlowRMS: true,
};

/**
 * Green (good rock) → yellow → red (bad rock).
 * t=0 is good, t=1 is bad.
 */
function rockQualityColor(t: number): [number, number, number] {
  t = Math.max(0, Math.min(1, t));
  if (t < 0.5) {
    // green → yellow
    const s = t / 0.5;
    return [s, 1, 0];
  } else {
    // yellow → red
    const s = (t - 0.5) / 0.5;
    return [1, 1 - s, 0];
  }
}

/** Optional category color map for categorical columns: value -> [r,g,b] (0-1) */
export type CategoryColorMap = Map<string, [number, number, number]>;

export function Boreholes3D({
  data,
  salvoData,
  mwdRawData,
  activeSensor,
  clampedRange,
  categoryColors,
  disabledCategories,
}: {
  data: CenterlineData;
  salvoData?: SalvoJsonData | null;
  mwdRawData?: Map<number, MwdRawScanData>;
  activeSensor?: MwdSensorName | null;
  clampedRange?: { min: number; max: number } | null;
  /** When set, the active column is categorical — use these colors instead of continuous ramp */
  categoryColors?: CategoryColorMap | null;
  /** Categories to render as dim/thin lines */
  disabledCategories?: ReadonlySet<string> | null;
}) {
  const { size } = useThree();
  const frames = useMemo(
    () => data.curve.computeFrenetFrames(FRAME_SAMPLES, false),
    [data.curve],
  );

  // Compute global min/max for the active sensor across all loaded scans (continuous only)
  const sensorRange = useMemo(() => {
    if (!activeSensor || !mwdRawData?.size || categoryColors) return null;
    let min = Infinity, max = -Infinity;
    for (const scanData of mwdRawData.values()) {
      for (const hole of scanData.holes) {
        const vals = hole.values[activeSensor];
        if (!vals) continue;
        for (const v of vals) {
          if (typeof v === "number") {
            if (v < min) min = v;
            if (v > max) max = v;
          }
        }
      }
    }
    if (!isFinite(min) || !isFinite(max) || min === max) return null;
    return { min, max };
  }, [activeSensor, mwdRawData, categoryColors]);

  const isCategorical = !!categoryColors;
  const useHeatmap = !!activeSensor && (isCategorical || !!sensorRange) && !!mwdRawData?.size;

  // Use clamped range if provided, otherwise fall back to full sensor range
  const colorRange = clampedRange && clampedRange.min < clampedRange.max ? clampedRange : sensorRange;

  // Build positions and colors arrays
  const lineData = useMemo(() => {
    if (!salvoData?.salvos?.length) return null;

    const positions: number[] = [];
    const colors: number[] = [];
    const dimPositions: number[] = [];
    const dimColors: number[] = [];

    for (const salvo of salvoData.salvos) {
      const t = Math.max(0, Math.min(1, salvo.faceStation / data.totalLength));
      const frameIdx = Math.round(t * FRAME_SAMPLES);

      const center = data.curve.getPointAt(t);
      const normal = frames.normals[frameIdx];
      const binormal = frames.binormals[frameIdx];
      const tangent = frames.tangents[frameIdx];

      // Look up MWD raw data for this scan
      const scanMwd = useHeatmap ? mwdRawData!.get(salvo.scan) : null;

      for (let holeIdx = 0; holeIdx < salvo.holes.length; holeIdx++) {
        const hole = salvo.holes[holeIdx];

        // Start point: position on the face
        const sx = center.x + hole.x * binormal.x - hole.y * normal.x;
        const sy = center.y + hole.x * binormal.y - hole.y * normal.y;
        const sz = center.z + hole.x * binormal.z - hole.y * normal.z;

        // Direction vectors for this hole
        const dx = tangent.x + hole.spreadX * binormal.x + hole.spreadY * (-normal.x);
        const dy = tangent.y + hole.spreadX * binormal.y + hole.spreadY * (-normal.y);
        const dz = tangent.z + hole.spreadX * binormal.z + hole.spreadY * (-normal.z);

        // Check if we have heatmap data for this hole
        const mwdHole = scanMwd?.holes[holeIdx];
        const sensorVals = mwdHole?.values[activeSensor!];

        if (useHeatmap && sensorVals && sensorVals.length > 1) {
          const depths = mwdHole!.depths;

          if (isCategorical) {
            // Categorical mode: look up color per segment from categoryColors map
            const catVals = sensorVals as string[];
            const defaultColor: [number, number, number] = [0.5, 0.5, 0.5];
            for (let i = 0; i < catVals.length - 1; i++) {
              const d0 = depths[i] / hole.depth;
              const d1 = depths[i + 1] / hole.depth;
              const p0x = sx + d0 * hole.depth * dx;
              const p0y = sy + d0 * hole.depth * dy;
              const p0z = sz + d0 * hole.depth * dz;
              const p1x = sx + d1 * hole.depth * dx;
              const p1y = sy + d1 * hole.depth * dy;
              const p1z = sz + d1 * hole.depth * dz;

              // If both endpoints are disabled categories, render as dim/thin
              const isDim0 = disabledCategories?.has(catVals[i]);
              const isDim1 = disabledCategories?.has(catVals[i + 1]);
              if (isDim0 && isDim1) {
                const dim = 0.15;
                dimPositions.push(p0x, p0y, p0z, p1x, p1y, p1z);
                dimColors.push(dim, dim, dim, dim, dim, dim);
              } else {
                positions.push(p0x, p0y, p0z, p1x, p1y, p1z);
                const c0 = categoryColors!.get(catVals[i]) ?? defaultColor;
                const c1 = categoryColors!.get(catVals[i + 1]) ?? defaultColor;
                colors.push(...c0, ...c1);
              }
            }
          } else {
            // Continuous mode: map numeric values to green-yellow-red ramp
            const numVals = sensorVals as number[];
            const { min, max } = sensorRange!;
            const range = max - min;
            const invert = SENSOR_HIGH_IS_BAD[activeSensor!] ?? false;
            // Clamp filter: hide segments entirely outside the visible range
            const clampLo = colorRange ? colorRange.min : min;
            const clampHi = colorRange ? colorRange.max : max;

            for (let i = 0; i < numVals.length - 1; i++) {
              const v0 = numVals[i];
              const v1 = numVals[i + 1];
              // Check if segment is outside the clamped range (filtered out)
              const isFiltered = (v0 < clampLo && v1 < clampLo) || (v0 > clampHi && v1 > clampHi);

              const d0 = depths[i] / hole.depth;
              const d1 = depths[i + 1] / hole.depth;

              const p0x = sx + d0 * hole.depth * dx;
              const p0y = sy + d0 * hole.depth * dy;
              const p0z = sz + d0 * hole.depth * dz;
              const p1x = sx + d1 * hole.depth * dx;
              const p1y = sy + d1 * hole.depth * dy;
              const p1z = sz + d1 * hole.depth * dz;

              if (isFiltered) {
                // Show filtered-out segments as very dim gray in a separate (thinner) group
                const dim = 0.15;
                dimPositions.push(p0x, p0y, p0z, p1x, p1y, p1z);
                dimColors.push(dim, dim, dim, dim, dim, dim);
              } else {
                positions.push(p0x, p0y, p0z, p1x, p1y, p1z);
                // Color based on full range so colors stay consistent
                let t0 = (v0 - min) / range;
                let t1 = (v1 - min) / range;
                if (!invert) { t0 = 1 - t0; t1 = 1 - t1; }
                const c0 = rockQualityColor(t0);
                const c1 = rockQualityColor(t1);
                colors.push(...c0, ...c1);
              }
            }
          }
        } else {
          const color = TYPE_COLORS[hole.type] ?? TYPE_COLORS.stoping;
          const ex = sx + hole.depth * dx;
          const ey = sy + hole.depth * dy;
          const ez = sz + hole.depth * dz;
          positions.push(sx, sy, sz, ex, ey, ez);
          colors.push(...color, ...color);
        }
      }
    }

    return {
      positions: new Float32Array(positions),
      colors: new Float32Array(colors),
      dimPositions: new Float32Array(dimPositions),
      dimColors: new Float32Array(dimColors),
    };
  }, [salvoData, data.curve, data.totalLength, frames, mwdRawData, activeSensor, colorRange, useHeatmap, categoryColors, disabledCategories]);

  // Fat line objects (LineSegments2) for heatmap mode
  const fatLineRef = useRef<LineSegments2 | null>(null);
  const fatMaterialRef = useRef<LineMaterial | null>(null);
  const dimMaterialRef = useRef<LineMaterial | null>(null);

  const fatGeometry = useMemo(() => {
    if (!lineData || !useHeatmap) return null;
    const geom = new LineSegmentsGeometry();
    geom.setPositions(lineData.positions);
    geom.setColors(lineData.colors);
    return geom;
  }, [lineData, useHeatmap]);

  const dimGeometry = useMemo(() => {
    if (!lineData || !useHeatmap || !lineData.dimPositions.length) return null;
    const geom = new LineSegmentsGeometry();
    geom.setPositions(lineData.dimPositions);
    geom.setColors(lineData.dimColors);
    return geom;
  }, [lineData, useHeatmap]);

  const fatMaterial = useMemo(() => {
    const mat = new LineMaterial({
      vertexColors: true,
      linewidth: 3, // pixels
      resolution: [size.width, size.height],
      toneMapped: false,
    });
    fatMaterialRef.current = mat;
    return mat;
  }, []);

  const dimMaterial = useMemo(() => {
    const mat = new LineMaterial({
      vertexColors: true,
      linewidth: 1, // thinner for filtered-out segments
      resolution: [size.width, size.height],
      toneMapped: false,
    });
    dimMaterialRef.current = mat;
    return mat;
  }, []);

  // Keep resolution updated
  useEffect(() => {
    if (fatMaterialRef.current) {
      fatMaterialRef.current.resolution.set(size.width, size.height);
    }
    if (dimMaterialRef.current) {
      dimMaterialRef.current.resolution.set(size.width, size.height);
    }
  }, [size.width, size.height]);

  // Thin line geometry for non-heatmap mode
  const thinGeometry = useMemo(() => {
    if (!lineData || useHeatmap) return null;
    const geom = new BufferGeometry();
    geom.setAttribute("position", new Float32BufferAttribute(lineData.positions, 3));
    geom.setAttribute("color", new Float32BufferAttribute(lineData.colors, 3));
    return geom;
  }, [lineData, useHeatmap]);

  const fatLine = useMemo(() => {
    if (!fatGeometry) return null;
    return new LineSegments2(fatGeometry, fatMaterial);
  }, [fatGeometry, fatMaterial]);

  const dimLine = useMemo(() => {
    if (!dimGeometry) return null;
    return new LineSegments2(dimGeometry, dimMaterial);
  }, [dimGeometry, dimMaterial]);

  if (!lineData) return null;

  if (useHeatmap && fatLine) {
    return (
      <group>
        <primitive
          ref={fatLineRef}
          object={fatLine}
        />
        {dimLine && (
          <primitive
            object={dimLine}
          />
        )}
      </group>
    );
  }

  if (thinGeometry) {
    return (
      <lineSegments geometry={thinGeometry}>
        <lineBasicMaterial vertexColors linewidth={1} />
      </lineSegments>
    );
  }

  return null;
}
